import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import type { QueryTypes } from '@seen/core/search'
import { useQueries } from '@tanstack/react-query'
import { useMemo } from 'react'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useSearchResourcePages } from '#src/hooks/useSearchResourcePages.ts'

export function usePractitioners(): ExtendedPractitioner[] {
  return useSortedPractitioners(ExtendedPractitioner.baseSearchQuery())
}

// Non-excluding variant for the Send Fax recipient list, where non-network
// practitioners must stay selectable. Clinician-facing callers keep
// usePractitioners, which hides non-network entries.
export function useFaxRecipientPractitioners(): ExtendedPractitioner[] {
  return useSortedPractitioners(ExtendedPractitioner.faxRecipientSearchQuery())
}

function useSortedPractitioners(query: QueryTypes): ExtendedPractitioner[] {
  const { data: practitioners } = useSearchResourcePages(
    ExtendedPractitioner,
    query
  )

  return useMemo(() => {
    if (!practitioners) {
      return EMPTY_PRACTITIONERS
    }

    // Internal first, then preferred, then the rest.
    return [...practitioners].sort(
      (a, b) =>
        Number(b.isInternal) - Number(a.isInternal) ||
        Number(b.isPreferred) - Number(a.isPreferred)
    )
  }, [practitioners])
}

const EMPTY_PRACTITIONERS: ExtendedPractitioner[] = []

interface UsePractitionersQueryResult {
  practitioners: ExtendedPractitioner[]
  isLoading: boolean
}

export function usePractitionersQuery(): UsePractitionersQueryResult {
  const { data: practitioners, isLoading } = useSearchResourcePages(
    ExtendedPractitioner,
    ExtendedPractitioner.baseSearchQuery()
  )

  return { practitioners: practitioners ?? EMPTY_PRACTITIONERS, isLoading }
}

interface UsePractitionersByIdsResult {
  practitionersById: Record<string, ExtendedPractitioner | undefined>
  isLoading: boolean
}

// Individual reads (not one bulk search) so each practitioner resolves from
// the shared cache other Practitioner reads already populate (nameplates,
// comboboxes, etc.), instead of forcing a fresh network round-trip.
export function usePractitionersByIds(
  ids: readonly string[]
): UsePractitionersByIdsResult {
  const medplum = useMedplum()

  const queries = useQueries({
    queries: ids.map((id) => ({
      queryKey: cacheKeys.readExtended(
        'Practitioner',
        id,
        'ExtendedPractitioner'
      ),
      queryFn: async () => medplum.readResourceClass(ExtendedPractitioner, id),
    })),
  })

  const queriesKey = queries.map((q) => q.dataUpdatedAt).join(',')
  const isLoading = queries.some((q) => q.isLoading)

  // queriesKey is a content hash of queries (fresh array identity every render) and captures its per-query data changes
  const practitionersById = useMemo(
    () =>
      Object.fromEntries(
        ids.map((id, index) => [id, queries[index]?.data] as const)
      ),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [ids, queriesKey]
  )

  return { practitionersById, isLoading }
}
